<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { classPlannerApi } from '@/api'
import type { ClassPlannerItem } from '@/api'
import { CalendarDays, Plus, List, ChevronLeft, ChevronRight, Settings2 } from 'lucide-vue-next'
import { AppButton, EmptyState, type PaletteHue } from '@/design-system/components'
import { useScheduleSettingsStore } from '@/store/ScheduleSettingsStore'
import ClassWeekGrid, { type GridDay } from '@/components/schedule/ClassWeekGrid.vue'
import AddLessonModal from '@/components/modals/AddLessonModal.vue'
import ManageScheduleModal from '@/components/modals/ManageScheduleModal.vue'

/** This class's week — a time-of-day grid (ClassWeekGrid) or a grouped list. */
const props = defineProps<{ classId: string; hue: PaletteHue }>()
const router = useRouter()
const settings = useScheduleSettingsStore()

function openItem(it: ClassPlannerItem) {
  if (it.type === 'Lesson') router.push(`/classes/${props.classId}/lesson/${it.id}`)
  else if (it.type === 'Assignment') router.push(`/classes/${props.classId}/assignment/${it.id}`)
}

const items = ref<ClassPlannerItem[]>([])
const loading = ref(true)
const showAdd = ref(false)
const showManage = ref(false)
const viewMode = ref<'week' | 'list'>('week')
const weekOffset = ref(0)

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function hhmm(s: string): string {
  const d = new Date(s)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const weekStart = computed(() => {
  const base = new Date()
  base.setDate(base.getDate() + weekOffset.value * 7)
  return settings.getWeekStart(base)
})
const weekEndDate = computed(() => {
  const d = new Date(weekStart.value)
  d.setDate(d.getDate() + 6)
  return d
})
const weekLabel = computed(() => {
  const s = weekStart.value
  const e = weekEndDate.value
  const sm = s.toLocaleDateString('en-GB', { month: 'short' })
  const em = e.toLocaleDateString('en-GB', { month: 'short' })
  return sm === em ? `${sm} ${s.getDate()}–${e.getDate()}` : `${sm} ${s.getDate()} – ${em} ${e.getDate()}`
})
const weekDays = computed<GridDay[]>(() => {
  const start = weekStart.value
  const jsStart = start.getDay()
  const todayIso = iso(new Date())
  return settings.schoolDayJsNumbers.map((jsDay) => {
    const offset = (jsDay - jsStart + 7) % 7
    const date = new Date(start)
    date.setDate(start.getDate() + offset)
    return {
      date,
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNumber: date.getDate(),
      dateString: iso(date),
      isToday: iso(date) === todayIso,
    }
  })
})
const itemsByDate = computed(() => {
  const map = new Map<string, ClassPlannerItem[]>()
  for (const it of items.value) {
    const date = it.dateTime.split('T')[0] ?? ''
    if (!date) continue
    if (!map.has(date)) map.set(date, [])
    map.get(date)!.push(it)
  }
  map.forEach((arr) => arr.sort((a, b) => a.dateTime.localeCompare(b.dateTime)))
  return map
})

interface DayGroup {
  key: string
  heading: string
  date: string
  rows: ClassPlannerItem[]
}

const grouped = computed<DayGroup[]>(() => {
  const map = new Map<string, ClassPlannerItem[]>()
  for (const it of items.value) {
    const key = it.dateTime.split('T')[0] ?? it.dateTime
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(it)
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, rows]) => {
      const d = new Date(key)
      return {
        key,
        heading: d.toLocaleDateString('en-GB', { weekday: 'long' }),
        date: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        rows: rows.sort((a, b) => a.dateTime.localeCompare(b.dateTime)),
      }
    })
})

function subline(it: ClassPlannerItem): string {
  if (it.type === 'Assignment') {
    return `Due · ${it.submittedCount ?? 0}/${it.totalCount ?? 0} in`
  }
  return it.room ?? ''
}

async function fetchData() {
  loading.value = true
  try {
    items.value = await classPlannerApi.getClassPlanner(
      props.classId,
      iso(weekStart.value),
      iso(weekEndDate.value),
    )
  } catch {
    items.value = []
  } finally {
    loading.value = false
  }
}
onMounted(async () => {
  if (!settings.isLoaded) await settings.fetchSettings()
  fetchData()
})
watch(weekOffset, fetchData)
</script>

<template>
  <div>
    <div class="sch__header">
      <span class="sch__eyebrow">This week · {{ items.length }} items</span>
      <div class="sch__actions">
        <div class="sch__toggle">
          <button type="button" :class="{ 'sch__toggle-on': viewMode === 'week' }" @click="viewMode = 'week'"><CalendarDays :size="14" /> Week</button>
          <button type="button" :class="{ 'sch__toggle-on': viewMode === 'list' }" @click="viewMode = 'list'"><List :size="14" /> List</button>
        </div>
        <AppButton variant="ghost" size="sm" @click="showManage = true"><Settings2 :size="14" /> Manage</AppButton>
        <AppButton variant="secondary" size="sm" @click="showAdd = true"><Plus :size="14" /> Add lesson</AppButton>
      </div>
    </div>
    <!-- Week view: navigable time grid -->
    <template v-if="viewMode === 'week'">
      <div class="sch__nav">
        <button type="button" class="sch__navbtn" aria-label="Previous week" @click="weekOffset--"><ChevronLeft :size="16" /></button>
        <span class="sch__weeklabel">{{ weekLabel }}</span>
        <button type="button" class="sch__navbtn" aria-label="Next week" @click="weekOffset++"><ChevronRight :size="16" /></button>
        <button v-if="weekOffset !== 0" type="button" class="sch__today" @click="weekOffset = 0">Today</button>
      </div>
      <ClassWeekGrid
        :days="weekDays"
        :items-by-date="itemsByDate"
        :hue="hue"
        :window-start-hour="settings.teachingStartHour"
        :window-end-hour="settings.teachingEndHour"
        @item-click="openItem"
        @add="() => (showAdd = true)"
      />
    </template>

    <!-- List view -->
    <EmptyState
      v-else-if="!loading && !items.length"
      :icon="CalendarDays"
      :hue="hue"
      :cta-icon="Plus"
      title="Nothing scheduled"
      body="Add lessons to this class's timetable — a one-off or a weekly repeat."
      cta="Add lesson"
      @action="showAdd = true"
    />
    <div v-else class="sch__days">
      <div v-for="g in grouped" :key="g.key">
        <div class="sch__dayhead">
          <span class="sch__dayname">{{ g.heading }}</span>
          <span class="sch__daydate">{{ g.date }}</span>
        </div>
        <div class="sch__card">
          <button
            v-for="(it, i) in g.rows"
            :key="it.id"
            type="button"
            class="sch__row"
            :class="{ 'sch__row--divided': i > 0 }"
            @click="openItem(it)"
          >
            <div class="sch__time">{{ hhmm(it.dateTime) }}</div>
            <span class="sch__tick" :style="{ background: hue.solid }" />
            <div class="sch__body">
              <div class="sch__title">{{ it.title || it.type }}</div>
              <div v-if="subline(it)" class="sch__sub">{{ subline(it) }}</div>
            </div>
          </button>
        </div>
      </div>
    </div>

    <AddLessonModal v-model="showAdd" :class-id="classId" @created="fetchData" />
    <ManageScheduleModal v-model="showManage" :class-id="classId" :hue="hue" @changed="fetchData" />
  </div>
</template>

<style scoped>
.sch__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.sch__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sch__toggle {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--surface-variant);
  border-radius: var(--radius-md);
}
.sch__toggle button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-500);
}
.sch__toggle-on {
  background: var(--surface);
  color: var(--text-primary) !important;
  font-weight: 600;
  box-shadow: var(--hairline-border);
}
.sch__nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.sch__navbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-sm);
  background: var(--surface);
  color: var(--ink-600);
  cursor: pointer;
}
.sch__navbtn:hover {
  background: var(--ink-50);
}
.sch__weeklabel {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 120px;
}
.sch__today {
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-pill);
  background: var(--surface);
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;
}
.sch__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.sch__days {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.sch__dayhead {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
  padding-left: 2px;
}
.sch__dayname {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
}
.sch__daydate {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: var(--ink-400);
}
.sch__card {
  border-radius: var(--radius-lg);
  box-shadow: var(--hairline-border);
  overflow: hidden;
  background: var(--surface);
}
.sch__row {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 13px 16px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
  transition: background var(--motion-fast);
}
.sch__row:hover {
  background: var(--ink-50);
}
.sch__row--divided {
  border-top: 1px solid var(--hairline);
}
.sch__time {
  width: 56px;
  flex-shrink: 0;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
}
.sch__tick {
  width: 3px;
  align-self: stretch;
  border-radius: 3px;
  flex-shrink: 0;
}
.sch__body {
  flex: 1;
  min-width: 0;
}
.sch__title {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}
.sch__sub {
  margin-top: 2px;
  font-size: 12.5px;
  color: var(--ink-500);
}
</style>
